<template>
  <div class="answer-block">
    <!-- No answer -->
    <div v-if="isEmpty" class="ab-empty">
      {{ $t('surveyBuilder.notAnswered') || 'Not answered' }}
    </div>

    <!-- text-field / text-area / numeric / date -->
    <div v-else-if="['text-field', 'text-area', 'numeric', 'date'].includes(type)" class="ab-value">
      {{ scalarValue }}
    </div>

    <!-- radio-button -->
    <div v-else-if="type === 'radio-button'">
      <div class="ab-chips">
        <span class="ab-chip">{{ selectedRadioLabel || '—' }}</span>
      </div>
      <get-data-answer-list :items="radioGetData" class="mt-2" />
      <dimitra-module-answer v-if="hasDimitra(radioDimitra)" :dimitra-module="radioDimitra" class="mt-3" />
      <div v-for="(ap, i) in radioActionPlans" :key="'ap' + i" class="ab-plan">
        <div class="ab-plan-title">
          <v-icon size="16" color="#117443">mdi-clipboard-check-outline</v-icon>
          {{ actionPlanLabel(ap) }}
        </div>
        <file-preview :files="planFiles(ap)" />
      </div>
    </div>

    <!-- check-boxes -->
    <div v-else-if="type === 'check-boxes'">
      <div v-for="(item, i) in checkboxItems" :key="i" class="ab-cb-item">
        <div class="ab-chips">
          <span class="ab-chip">{{ optionLabel(item.optionId) || '—' }}</span>
        </div>
        <get-data-answer-list
          v-if="item.getDataResponses && item.getDataResponses.length"
          :items="item.getDataResponses"
          class="mt-2"
        />
        <dimitra-module-answer v-if="hasDimitra(item.dimitraModule)" :dimitra-module="item.dimitraModule" class="mt-3" />
        <div v-for="(ap, ai) in item.actionPlanResponses" :key="'ap' + ai" class="ab-plan">
          <div class="ab-plan-title">
            <v-icon size="16" color="#117443">mdi-clipboard-check-outline</v-icon>
            {{ actionPlanLabel(ap) }}
          </div>
          <file-preview :files="planFiles(ap)" />
        </div>
      </div>
    </div>

    <!-- digital-signature -->
    <div v-else-if="type === 'digital-signature'">
      <a v-if="signatureLink" :href="signatureLink" target="_blank" rel="noopener" class="ab-sig-link">
        <img :src="signatureLink" alt="signature" class="ab-sig" />
      </a>
      <div v-else class="ab-sig-empty">
        <v-icon size="26" color="#979797">mdi-draw</v-icon>
        <span>Signature on file</span>
      </div>
    </div>

    <!-- attach-files -->
    <div v-else-if="type === 'attach-files'">
      <file-preview :files="files" />
    </div>

    <!-- get-data -->
    <div v-else-if="type === 'get-data'">
      <get-data-answer-list :items="getDataItems" />
    </div>

    <!-- dimitra-module -->
    <div v-else-if="type === 'dimitra-module'">
      <dimitra-module-answer :dimitra-module="question.dimitraModule || {}" />
    </div>

    <!-- fallback -->
    <div v-else class="ab-empty">{{ fallbackText }}</div>

    <!-- repeat submissions -->
    <div v-if="repeatDates.length > 1" class="ab-repeat">
      {{ $t('surveyBuilder.repeatSubmissions') || 'Repeat submissions' }}: {{ repeatDates.join(', ') }}
    </div>
  </div>
</template>

<script>
import moment from "moment";
import GetDataAnswerList from "./GetDataAnswerList.vue";
import FilePreview from "./FilePreview.vue";
import DimitraModuleAnswer from "./DimitraModuleAnswer.vue";

export default {
  name: "ResponseAnswerBlock",
  components: {
    "get-data-answer-list": GetDataAnswerList,
    "file-preview": FilePreview,
    "dimitra-module-answer": DimitraModuleAnswer,
  },
  props: {
    question: { type: Object, required: true },
  },
  computed: {
    type() {
      return this.question.questionType;
    },
    response() {
      return this.question.response;
    },
    isEmpty() {
      const r = this.response;
      if (r === null || r === undefined) return true;
      if (Array.isArray(r)) return r.length === 0;
      if (typeof r === "object") {
        if ("value" in r) {
          const v = r.value;
          return v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
        }
        return Object.keys(r).length === 0;
      }
      return r === "";
    },
    scalarValue() {
      const r = this.response;
      const v = r && typeof r === "object" && "value" in r ? r.value : r;
      return v !== null && v !== undefined ? String(v) : "—";
    },
    selectedRadioLabel() {
      const id = this.optionIdFrom(this.response && this.response.value);
      return this.optionLabel(id);
    },
    radioGetData() {
      return (this.response && this.response.getDataResponses) || [];
    },
    radioActionPlans() {
      return (this.response && this.response.actionPlanResponses) || [];
    },
    checkboxItems() {
      const arr = Array.isArray(this.response)
        ? this.response
        : Array.isArray(this.response && this.response.value)
        ? this.response.value
        : [];
      return arr.map((item) => ({
        optionId: this.optionIdFrom(item && item.value !== undefined ? item.value : item),
        getDataResponses: (item && item.getDataResponses) || [],
        actionPlanResponses: (item && item.actionPlanResponses) || [],
        dimitraModule: (item && item.dimitraModule) || null,
      }));
    },
    radioDimitra() {
      return (this.response && this.response.dimitraModule) || null;
    },
    signatureLink() {
      const v = this.response && this.response.value;
      if (!v) return null;
      return v.attachmentLink || v.link || (typeof v === "string" ? v : null);
    },
    files() {
      const v = this.response && this.response.value;
      const arr = Array.isArray(v) ? v : Array.isArray(this.response) ? this.response : [];
      return arr.map((f) => ({
        fileName: f.fileName || f.name || "",
        fileUrl: f.fileUrl || f.attachmentLink || f.link || "",
        comment: f.comment || "",
      }));
    },
    getDataItems() {
      const r = this.response;
      if (Array.isArray(r)) return r;
      if (r && Array.isArray(r.getDataResponses)) return r.getDataResponses;
      if (r && Array.isArray(r.value)) return r.value;
      return [];
    },
    fallbackText() {
      try {
        return JSON.stringify(this.response);
      } catch (e) {
        return "—";
      }
    },
    repeatDates() {
      return (this.question.responses || [])
        .map((r) => r.repetitionDate)
        .filter(Boolean)
        .map((d) => moment(d).format("DD/MM/YY"));
    },
  },
  methods: {
    hasDimitra(dm) {
      return !!(dm && typeof dm === "object" && Object.keys(dm).length);
    },
    optionIdFrom(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === "object") return value.id || value._id || null;
      return value;
    },
    optionLabel(optionId) {
      if (!optionId) return null;
      const opt = (this.question.options || []).find((o) => String(o._id) === String(optionId));
      return opt ? opt.label : null;
    },
    actionPlanLabel(a) {
      if (!a) return "";
      if (typeof a === "string") return a;
      return a.title || a.name || a.label || JSON.stringify(a);
    },
    planFiles(ap) {
      const files = (ap && ap.files) || [];
      return files.map((f) => ({
        fileName: f.fileName || f.name || "",
        fileUrl: f.fileUrl || f.attachmentLink || f.link || "",
      }));
    },
  },
};
</script>

<style scoped>
.ab-empty {
  color: #979797;
  font-style: italic;
}
.ab-value {
  font-size: 14px;
  color: #2e2f31;
  line-height: 1.5;
  white-space: pre-wrap;
}
/* chips */
.ab-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.ab-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: #0ebf67;
  background: #e5f8f1;
  border: 1px solid #0ebf67;
}
.ab-cb-item {
  margin-bottom: 12px;
}
.ab-cb-item:last-child {
  margin-bottom: 0;
}
.ab-plan {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f8f9fb;
  border-radius: 6px;
}
.ab-plan-title {
  font-size: 13px;
  font-weight: 600;
  color: #117443;
  margin-bottom: 4px;
}
/* signature */
.ab-sig-link {
  display: inline-block;
  cursor: pointer;
}
.ab-sig {
  max-width: 160px;
  max-height: 90px;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  background: #f8f8f8;
}
.ab-sig-empty {
  width: 160px;
  height: 90px;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  background: #f8f8f8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 11px;
  color: #787878;
}
/* files */
.ab-file {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.ab-file:last-child {
  margin-bottom: 0;
}
.ab-file-icon {
  color: #117443 !important;
}
.ab-link {
  font-size: 14px;
  color: #117443;
  text-decoration: underline;
}
.ab-link:hover {
  color: #0ebf67;
}
.ab-comment {
  font-size: 12px;
  color: #787878;
}
.ab-repeat {
  margin-top: 8px;
  font-size: 12px;
  color: #787878;
}
</style>
